import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useClassificationProgress } from "@/hooks/useClassificationProgress";
import { upgradeTwitterAvatar } from "@/lib/twitterAvatar";

export interface LeaderboardEntry {
  id: string;
  handle: string;
  displayName: string;
  avatar: string;
  followers: string;
  totalCalls: number;
  totalActionableCalls: number;
  winRate: number;
  avgPnl: number;
  bestCall: number;
  worstCall: number;
  avgPnlPerDay: number;
  avgDaysHeld: number;
  netPnlCaptured: number;
  score: number;
  claimed: boolean;
}

export function useLeaderboard() {
  const { remaining } = useClassificationProgress("__global__");

  return useQuery({
    queryKey: ["leaderboard"],
    refetchOnMount: "always",
    queryFn: async (): Promise<LeaderboardEntry[]> => {
      const { data, error } = await supabase.rpc("get_leaderboard_stats");
      if (error) throw error;

      return (data || []).map((row: any) => ({
        id: row.x_user_id,
        handle: row.handle,
        displayName: row.display_name || row.handle,
        avatar: upgradeTwitterAvatar(
          row.profile_image_url ||
          `https://api.dicebear.com/9.x/bottts/svg?seed=${row.x_user_id}`
        ),
        followers: "",
        totalCalls: Number(row.total_positions),
        totalActionableCalls: Number(row.total_calls ?? 0),
        winRate: Number(row.win_rate),
        avgPnl: Number(row.avg_pnl),
        bestCall: Number(row.best_call),
        worstCall: Number(row.worst_call),
        avgPnlPerDay: Number(row.avg_pnl_per_day),
        avgDaysHeld: Number(row.avg_days_held),
        netPnlCaptured: Number(row.total_positions) * Number(row.avg_pnl),
        score: Math.log10(Math.max(Number(row.total_positions), 1)) * Number(row.avg_pnl),
        claimed: row.claimed ?? false,
      }));
    },
    refetchInterval: remaining && remaining > 0 ? 10_000 : false,
  });
}
